import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Megaphone, Linkedin, FileText, FileDown, Pencil, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

const BLANK = {
  name: "", role: "", bio: "", session_title: "", day_id: "",
  photo_url: "", bio_url: "", deck_url: "", linkedin_url: "", order: 0,
};

export default function Speakers() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "faculty");
  const [speakers, setSpeakers] = useState([]);
  const [days, setDays] = useState([]);
  const [filterDay, setFilterDay] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(null); // speaker id while summarising
  const [aiResults, setAiResults] = useState({}); // by speaker id
  const [enriching, setEnriching] = useState(null);
  const [castmagicSpeaker, setCastmagicSpeaker] = useState(null);
  const [castmagicForm, setCastmagicForm] = useState({ transcript_text: "", slides_url: "" });
  const [castBusy, setCastBusy] = useState(false);

  const load = async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/speakers").then((r) => r.data),
        api.get("/days").then((r) => r.data),
      ]);
      setSpeakers(s); setDays(d);
    } catch {
      toast.error("Could not load speakers");
    }
  };

  useEffect(() => { load(); }, []);

  const dayById = useMemo(() => Object.fromEntries(days.map((d) => [d.id, d])), [days]);

  const visible = useMemo(() => {
    if (filterDay === "all") return speakers;
    if (filterDay === "none") return speakers.filter((s) => !s.day_id);
    return speakers.filter((s) => s.day_id === filterDay);
  }, [filterDay, speakers]);

  const startCreate = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const startEdit = (s) => { setEditing(s); setForm({ ...BLANK, ...s, day_id: s.day_id || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = { ...form, day_id: form.day_id || null };
    try {
      if (editing) {
        await api.put(`/speakers/${editing.id}`, payload);
        toast.success("Speaker updated");
      } else {
        await api.post("/speakers", payload);
        toast.success("Speaker added");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (s) => {
    if (!(await confirm({ title: `Remove ${s.name} from the speaker line-up?`, destructive: true }))) return;
    try { await api.delete(`/speakers/${s.id}`); toast.success("Speaker removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const enrichFromBio = async (s) => {
    setEnriching(s.id);
    try {
      const { data } = await api.post(`/speakers/${s.id}/enrich-from-bio`);
      const fields = (data.updated_fields || []).filter((f) => f !== "updated_at");
      if (fields.length) toast.success(`AI filled: ${fields.join(", ")}`);
      else toast.message("Nothing to add", { description: "All editable fields already had content." });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    } finally { setEnriching(null); }
  };

  const openCastmagic = (s) => {
    setCastmagicSpeaker(s);
    setCastmagicForm({ transcript_text: s.transcript_text || "", slides_url: s.slides_url || "" });
  };

  const runCastmagic = async () => {
    if (!castmagicForm.transcript_text || castmagicForm.transcript_text.length < 20) {
      toast.error("Paste a transcript (at least 20 chars) first");
      return;
    }
    setCastBusy(true);
    try {
      await api.post(`/speakers/${castmagicSpeaker.id}/castmagic`, castmagicForm);
      toast.success("Castmagic summary saved to speaker card");
      setCastmagicSpeaker(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Castmagic failed");
    } finally { setCastBusy(false); }
  };

  const summarise = async (s) => {
    if (!s.bio) {
      toast.message("No bio text yet", { description: "Paste a transcript or bio into the speaker's bio field first, then summarise." });
      return;
    }
    setAiBusy(s.id);
    try {
      const { data } = await api.post("/ai/summarise", { title: s.name, content: s.bio });
      setAiResults((r) => ({ ...r, [s.id]: data.summary }));
      toast.success("AI summary ready");
    } catch (e) {
      toast.error("Summary failed");
    } finally { setAiBusy(null); }
  };

  return (
    <div className="space-y-8" data-testid="speakers-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Speakers</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">On the stage.</h1>
          <p className="text-sm text-[#525252] max-w-2xl mt-2">
            Programme speakers across the week. Each card links to a full bio. Paste a transcript or notes
            into a speaker's bio field then click <em>Summarise</em> to generate a Castmagic-style headline + key points.
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3] uppercase tracking-wide text-xs font-semibold"
                      data-testid="speaker-add-btn">
                <Plus size={14} className="mr-2" /> Add speaker
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {editing ? "Edit speaker" : "Add speaker"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                <Field label="Name *" value={form.name} set={(v) => setForm((f) => ({ ...f, name: v }))} testid="speaker-name-input" />
                <Field label="Role / title" value={form.role} set={(v) => setForm((f) => ({ ...f, role: v }))} testid="speaker-role-input" />
                <Field label="Session title" value={form.session_title} set={(v) => setForm((f) => ({ ...f, session_title: v }))} testid="speaker-session-input" />
                <div>
                  <Label className="text-xs text-[#525252]">Day</Label>
                  <Select value={form.day_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, day_id: v === "none" ? "" : v }))}>
                    <SelectTrigger className="rounded-none mt-1 h-10" data-testid="speaker-day-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {days.map((d) => (
                        <SelectItem key={d.id} value={d.id}>Day {d.day_number} — {d.title || ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Photo URL" value={form.photo_url} set={(v) => setForm((f) => ({ ...f, photo_url: v }))} testid="speaker-photo-input" />
                <Field label="Bio PDF / link" value={form.bio_url} set={(v) => setForm((f) => ({ ...f, bio_url: v }))}
                       placeholder="Dropbox / Drive PDF" testid="speaker-bio-url-input" />
                <Field label="Deck URL" value={form.deck_url} set={(v) => setForm((f) => ({ ...f, deck_url: v }))} testid="speaker-deck-input" />
                <Field label="LinkedIn" value={form.linkedin_url} set={(v) => setForm((f) => ({ ...f, linkedin_url: v }))} testid="speaker-linkedin-input" />
                <div>
                  <Label className="text-xs text-[#525252]">Bio / transcript text</Label>
                  <Textarea
                    value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                    placeholder="Optional. Paste a transcript or bio here to enable the AI summarise button."
                    className="rounded-none mt-1 min-h-[120px] text-sm" data-testid="speaker-bio-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#525252]">Display order</Label>
                  <Input type="number" value={form.order}
                         onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) || 0 }))}
                         className="rounded-none mt-1 h-10 w-32" data-testid="speaker-order-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-[#0A0A0A]" data-testid="speaker-cancel-btn">Cancel</Button>
                <Button onClick={save} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3] uppercase tracking-wide text-xs font-semibold"
                        data-testid="speaker-save-btn">{editing ? "Update" : "Add"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </header>

      {/* Day filter chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="speaker-day-filter">
        <span className="label-eyebrow">Filter by day</span>
        <FilterChip active={filterDay === "all"} onClick={() => setFilterDay("all")}>All</FilterChip>
        {days.map((d) => (
          <FilterChip key={d.id} active={filterDay === d.id} onClick={() => setFilterDay(d.id)}>
            Day {d.day_number}
          </FilterChip>
        ))}
        <FilterChip active={filterDay === "none"} onClick={() => setFilterDay("none")}>Unassigned</FilterChip>
      </div>

      {visible.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E7EB] p-10 text-center">
          <Megaphone size={28} className="mx-auto text-[#525252]" />
          <div className="mt-3 font-medium text-[#0A0A0A]">No speakers listed yet</div>
          <p className="mt-1 text-xs text-[#525252]">
            {isAdmin ? "Click 'Add speaker' to start the line-up." : "Faculty hasn't published the speaker line-up yet."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((s) => {
            const day = s.day_id ? dayById[s.day_id] : null;
            const summary = aiResults[s.id];
            return (
              <article key={s.id} className="border border-[#E5E7EB] bg-white p-5 flex flex-col gap-3"
                       data-testid={`speaker-card-${s.id}`}>
                <header className="flex items-start gap-3">
                  {s.photo_url ? (
                    <img src={s.photo_url} alt={s.name} className="w-14 h-14 object-cover bg-[#F3F4F6]" />
                  ) : (
                    <div className="w-14 h-14 bg-[#f4b847] text-[#0A0A0A] flex items-center justify-center text-lg font-bold uppercase">
                      {(s.name || "?").slice(0, 2)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]" data-testid={`speaker-name-${s.id}`}>
                      {s.name}
                    </div>
                    {s.role && <div className="text-xs text-[#525252]">{s.role}</div>}
                    {day && (
                      <div className="text-[10px] uppercase tracking-wider text-[#2596be] mt-1">
                        Day {day.day_number} {day.title ? `· ${day.title}` : ""}
                      </div>
                    )}
                  </div>
                </header>
                {s.session_title && (
                  <div className="text-sm font-medium text-[#0A0A0A]">{s.session_title}</div>
                )}
                {s.bio && (
                  <p className="text-xs text-[#525252] leading-relaxed whitespace-pre-wrap">{s.bio}</p>
                )}
                {summary && (
                  <div className="border-l-2 border-[#2596be] bg-[#E6F4FA] p-3 space-y-1" data-testid={`speaker-ai-${s.id}`}>
                    <div className="label-eyebrow text-[#1e7da3] flex items-center gap-1">
                      <Sparkles size={11} /> AI summary
                    </div>
                    <p className="text-xs whitespace-pre-wrap text-[#0A0A0A]">{summary}</p>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-wider font-semibold mt-1">
                  {s.bio_url && (
                    <a href={s.bio_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`speaker-bio-link-${s.id}`}
                       className="inline-flex items-center gap-1.5 text-[#2596be] hover:text-[#1e7da3]">
                      <FileText size={12} /> Bio
                    </a>
                  )}
                  {s.deck_url && (
                    <a href={s.deck_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`speaker-deck-link-${s.id}`}
                       className="inline-flex items-center gap-1.5 text-[#2596be] hover:text-[#1e7da3]">
                      <FileDown size={12} /> Deck
                    </a>
                  )}
                  {s.linkedin_url && (
                    <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`speaker-linkedin-${s.id}`}
                       className="inline-flex items-center gap-1.5 text-[#2596be] hover:text-[#1e7da3]">
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 border-t border-[#E5E7EB] pt-3 mt-1">
                  <Button onClick={() => summarise(s)} disabled={aiBusy === s.id} variant="outline"
                          className="h-8 rounded-none border-[#2596be] text-[#2596be] hover:bg-[#E6F4FA] text-[11px] uppercase tracking-wide"
                          data-testid={`speaker-summarise-${s.id}`}>
                    <Sparkles size={12} className="mr-1.5" /> {aiBusy === s.id ? "Summarising…" : "Summarise"}
                  </Button>
                  {isAdmin && s.bio_url && (
                    <Button onClick={() => enrichFromBio(s)} disabled={enriching === s.id} variant="outline"
                            className="h-8 rounded-none border-[#2596be] text-[#2596be] hover:bg-[#E6F4FA] text-[11px] uppercase tracking-wide"
                            data-testid={`speaker-enrich-${s.id}`}>
                      <Sparkles size={12} className="mr-1.5" /> {enriching === s.id ? "…" : "AI fill bio"}
                    </Button>
                  )}
                  {isAdmin && (
                    <Button onClick={() => openCastmagic(s)} variant="outline"
                            className="h-8 rounded-none border-[#f4b847] text-[#7A5B17] hover:bg-[#FEF3D0] text-[11px] uppercase tracking-wide"
                            data-testid={`speaker-castmagic-btn-${s.id}`}>
                      <Sparkles size={12} className="mr-1.5" /> Castmagic
                    </Button>
                  )}
                  {isAdmin && (
                    <>
                      <Button onClick={() => startEdit(s)} variant="outline"
                              className="h-8 rounded-none border-[#0A0A0A] text-[11px] uppercase tracking-wide"
                              data-testid={`speaker-edit-${s.id}`}>
                        <Pencil size={12} className="mr-1.5" /> Edit
                      </Button>
                      <Button onClick={() => remove(s)} variant="outline"
                              className="h-8 rounded-none border-[#e93e36] text-[#e93e36] hover:bg-[#FDEDED] text-[11px] uppercase tracking-wide"
                              data-testid={`speaker-delete-${s.id}`}>
                        <Trash2 size={12} className="mr-1.5" /> Remove
                      </Button>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Castmagic dialog */}
      <Dialog open={!!castmagicSpeaker} onOpenChange={(o) => !o && setCastmagicSpeaker(null)}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Castmagic summary · {castmagicSpeaker?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#525252]">
              Paste the session transcript below. AI generates a headline, 3-5 key points, student implications, and notable quotes — saved to this speaker's card for everyone to see.
            </p>
            <div>
              <Label className="label-eyebrow">Transcript text</Label>
              <Textarea
                className="rounded-none mt-2 min-h-[200px] text-sm"
                value={castmagicForm.transcript_text}
                onChange={(e) => setCastmagicForm((f) => ({ ...f, transcript_text: e.target.value }))}
                placeholder="Paste the full transcript here…"
                data-testid="castmagic-transcript-input"
              />
            </div>
            <Field
              label="Slides URL (optional)"
              value={castmagicForm.slides_url}
              set={(v) => setCastmagicForm((f) => ({ ...f, slides_url: v }))}
              placeholder="https://… (Dropbox / Drive PDF)"
              testid="castmagic-slides-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCastmagicSpeaker(null)} className="rounded-none border-[#0A0A0A]" data-testid="castmagic-cancel-btn">Cancel</Button>
            <Button onClick={runCastmagic} disabled={castBusy} className="rounded-none bg-[#f4b847] hover:bg-[#dba134] text-[#0A0A0A]" data-testid="castmagic-run-btn">
              <Sparkles size={13} className="mr-2" /> {castBusy ? "Summarising…" : "Generate summary"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, set, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs text-[#525252]">{label}</Label>
      <Input
        value={value || ""} onChange={(e) => set(e.target.value)}
        placeholder={placeholder}
        className="rounded-none mt-1 h-10" data-testid={testid}
      />
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
            className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] border transition-colors ${
              active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                     : "bg-white text-[#0A0A0A] border-[#E5E7EB] hover:border-[#0A0A0A]"
            }`}>
      {children}
    </button>
  );
}
